import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
    CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CTable,
    CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CSpinner,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CForm, CFormInput,
    CFormCheck, CInputGroup, CPagination, CPaginationItem
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilArrowLeft, cilReload, cilLockLocked, cilWarning } from '@coreui/icons';
import { toast } from 'react-toastify';
import './UserManagementPage.css';
import { useTranslation } from 'react-i18next';

const UserManagementPage = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ type: null, data: null, visible: false }); // type: 'edit', 'password'
    
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });

    const fetchUsers = async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/users/?page=${page}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setUsers(res.data.results);
            setPagination({
                currentPage: page,
                totalPages: Math.ceil(res.data.count / 20) // Assuming page size is 20
            });
        } catch (err) {
            toast.error(t('common.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(1);
    }, []);

    const generatePassword = (length = 12) => {
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
        let password = "";
        for (let i = 0, n = charset.length; i < length; ++i) {
            password += charset.charAt(Math.floor(Math.random() * n));
        }
        setModal(prev => ({ ...prev, data: { ...prev.data, password } }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setModal(prev => ({
            ...prev,
            data: { ...prev.data, [name]: type === 'checkbox' ? checked : value }
        }));
    };

    const handleSave = async () => {
        const { type, data } = modal;
        const token = localStorage.getItem('token');
        
        if (type === 'password' && data.password.length < 8) {
            toast.warn("Password too short.");
            return;
        }

        try {
            if (type === 'edit') {
                await axios.patch(`/api/users/${data.id}/`, data, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success(t('users.update_success'));
            } else if (type === 'password') {
                await axios.post(`/api/users/${data.id}/set-password/`, { new_password: data.password }, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success(t('users.password_success'));
            }
            closeModal();
            fetchUsers(pagination.currentPage);
        } catch (error) {
            toast.error(t('common.error'));
        }
    };

    const openModal = (type, data = null) => {
        let initialData = { ...data };
        if (type === 'password') {
            initialData.password = '';
        }
        setModal({ type, data: initialData, visible: true });
    };

    const closeModal = () => setModal({ type: null, data: null, visible: false });

    const getModalTitle = () => {
        if (modal.type === 'edit') return t('users.edit_modal_title');
        if (modal.type === 'password') return `${t('users.password_modal_title')} ${modal.data?.username}`;
        return '';
    };

    return (
        <div className="bg-light min-vh-100 p-4">
            <CContainer fluid>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>{t('users.title')}</h2>
                    <CButton color="secondary" as={Link} to="/dashboard">
                        <CIcon icon={cilArrowLeft} className="me-2" /> {t('common.back')}
                    </CButton>
                </div>

                <CCard>
                    <CCardHeader>
                        {t('users.list_title')}
                    </CCardHeader>
                    <CCardBody>
                        {loading ? <div className="text-center"><CSpinner /></div> : (
                            <div className="table-responsive">
                                <CTable align="middle" className="mb-0 border" hover responsive>
                                    <CTableHead color="light">
                                        <CTableRow>
                                            <CTableHeaderCell>{t('users.username')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('users.fullname')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('users.email')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('users.role')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('users.actions')}</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {users.map(user => (
                                            <CTableRow key={user.id}>
                                                <CTableDataCell>{user.username}</CTableDataCell>
                                                <CTableDataCell>{user.first_name} {user.last_name}</CTableDataCell>
                                                <CTableDataCell>{user.email}</CTableDataCell>
                                                <CTableDataCell>
                                                    {user.is_superuser ? t('users.role_superuser') : (user.is_staff ? t('users.role_admin') : t('users.role_user'))}
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    {user.is_superuser ? (
                                                        <span className="text-muted fst-italic">
                                                            <CIcon icon={cilWarning} className="me-1" />
                                                            {t('users.unavailable')}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <CButton color="secondary" size="sm" className="me-2" onClick={() => openModal('edit', user)}>
                                                                <CIcon icon={cilPencil} />
                                                            </CButton>
                                                            <CButton color="warning" size="sm" className="me-2" onClick={() => openModal('password', user)}>
                                                                <CIcon icon={cilLockLocked} />
                                                            </CButton>
                                                        </>
                                                    )}
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            </div>
                        )}
                        {pagination.totalPages > 1 && (
                            <CPagination className="mt-3 justify-content-center">
                                {[...Array(pagination.totalPages).keys()].map(num => (
                                    <CPaginationItem
                                        key={num}
                                        active={pagination.currentPage === num + 1}
                                        onClick={() => fetchUsers(num + 1)}
                                    >
                                        {num + 1}
                                    </CPaginationItem>
                                ))}
                            </CPagination>
                        )}
                    </CCardBody>
                </CCard>
            </CContainer>

            <CModal alignment="center" visible={modal.visible} onClose={closeModal}>
                <CModalHeader><CModalTitle>{getModalTitle()}</CModalTitle></CModalHeader>
                <CModalBody>
                    <CForm>
                        {modal.type === 'edit' && (
                            <>
                                <CFormInput label={t('users.firstname')} name="first_name" value={modal.data?.first_name} onChange={handleInputChange} className="mb-3" />
                                <CFormInput label={t('users.lastname')} name="last_name" value={modal.data?.last_name} onChange={handleInputChange} className="mb-3" />
                                <CFormInput label={t('users.email')} type="email" name="email" value={modal.data?.email} onChange={handleInputChange} className="mb-3" />
                                <CFormCheck label={t('users.is_staff_label')} name="is_staff" checked={modal.data?.is_staff} onChange={handleInputChange} className="mb-3" />
                            </>
                        )}
                        {modal.type === 'password' && (
                            <CInputGroup className="mb-3">
                                <CFormInput 
                                  label={t('users.new_password')} 
                                  type="text" 
                                  name="password" 
                                  value={modal.data?.password} 
                                  onChange={handleInputChange} 
                                  minLength={8}
                                />
                                <CButton type="button" color="secondary" variant="outline" onClick={() => generatePassword()}>
                                    <CIcon icon={cilReload} /> {t('users.generate')}
                                </CButton>
                            </CInputGroup>
                        )}
                    </CForm>
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={closeModal}>{t('common.cancel')}</CButton>
                    <CButton color="primary" onClick={handleSave}>{t('common.save')}</CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default UserManagementPage;